// Canonical API paths for the SPA (Phase 1 / T1.6B).
//
// The server's tenancy lives in the URL — `/api/b/:businessId/...` for a
// Business surface — so the client must put it there too. This is the one place
// that shape is written down on this side, mirroring the server's own
// `tenant-paths.ts`, so no screen composes a tenant path by hand.
//
// The Business id comes from the CANONICAL ROUTE (`useBusinessId()`), which is
// the tenant the server just proved on the request that rendered the screen.
// It is never derived from a Location id, a stored preference, a first or
// last-active business, or an old org/site id: those are the shapes that let a
// request act on a tenant the caller did not name.

/** `/api/b/:businessId/<tail>`. `tail` carries no leading slash. */
export function businessApi(businessId: string, tail: string): string {
  if (!businessId) throw new Error('businessApi: no Business in scope — a Business API call needs a canonical route');
  if (tail.startsWith('/')) throw new Error(`businessApi: tail must not start with '/': ${tail}`);
  return `/api/b/${encodeURIComponent(businessId)}/${tail}`;
}

/**
 * `/api/b/:businessId/l/:locationId/<tail>` — a LOCATION surface.
 *
 * BOTH ids are required and BOTH slots are positional. A Location is only ever
 * meaningful inside its Business, and the server proves that pairing on every
 * request; a client that could supply one id would have to infer the other, and
 * inferring is how an id ends up in the wrong slot.
 *
 * Passing an empty id throws rather than emitting `/api/b//l/x/…`, which would
 * reach the server as a malformed tenant.
 */
export function locationApi(businessId: string, locationId: string, tail: string): string {
  if (!businessId) throw new Error('locationApi: no Business in scope — a Location API call needs a canonical route');
  if (!locationId) throw new Error('locationApi: no Location in scope — a Location API call needs a canonical route');
  if (tail.startsWith('/')) throw new Error(`locationApi: tail must not start with '/': ${tail}`);
  const base = `/api/b/${encodeURIComponent(businessId)}/l/${encodeURIComponent(locationId)}`;
  return tail ? `${base}/${tail}` : base;
}

/**
 * The GBP connect entry point: `/api/b/:businessId/l/:locationId/gbp/connect/start`.
 *
 * A top-level browser navigation (plain `<a href>`), never a fetch — the server
 * answers with the OAuth redirect to Google. `from=setup` asks the server to
 * come back to the setup wizard; `switch=1` asks Google to show its account
 * chooser. The server only ever PICKS between fixed shapes from these, it never
 * takes a path from them.
 */
export function gbpConnectHref(
  businessId: string,
  locationId: string,
  opts: { from?: 'setup'; switchAccount?: boolean } = {},
): string {
  const params = new URLSearchParams();
  if (opts.from) params.set('from', opts.from);
  if (opts.switchAccount) params.set('switch', '1');
  const query = params.toString();
  const base = locationApi(businessId, locationId, 'gbp/connect/start');
  return query ? `${base}?${query}` : base;
}

/**
 * The Search Console connect entry point:
 * `/api/b/:businessId/l/:locationId/gsc/connect/start`.
 *
 * Same contract as `gbpConnectHref`: a top-level browser navigation (plain
 * `<a href>`), never a fetch — the server answers with the OAuth redirect to
 * Google. `switch=1` asks Google to show its account chooser. The server only
 * ever PICKS between fixed shapes from the query, it never takes a path from
 * it. The retired pre-tenancy `/auth/gsc/…` start route no longer exists (404).
 */
export function gscConnectHref(
  businessId: string,
  locationId: string,
  opts: { switchAccount?: boolean } = {},
): string {
  const base = locationApi(businessId, locationId, 'gsc/connect/start');
  return opts.switchAccount ? `${base}?switch=1` : base;
}

/**
 * The "Let pageboss set it up" consent: `…/gsc/setup/consent`. A top-level
 * navigation too — it leaves for Google's WRITE-scoped consent screen. The
 * retired pre-tenancy `/auth/gsc/setup/…` start route no longer exists (404).
 */
export function gscSetupConsentHref(businessId: string, locationId: string): string {
  return locationApi(businessId, locationId, 'gsc/setup/consent');
}
